import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import OutletSearch from '../../SelectSearch/OutletSearch'
import Navbar from '../../Navbar'
import Footer from '../../Footer'
import PageStatus from '../../PageStatus/PageStatus'
import { gramVariations, kgVariations, literVariations, milliliterVariations } from '../../../data/unitVariations'
import CentralInventorySummaryTable from './CentralInventorySummaryTable'
import CentralInventoryConsumptionTable from './CentralInventoryConsumptionTable'
import CentralKitchenInventoryModal from './CentralKitchenInventoryModal'
import { normalizeUOM } from '../../../utils/quantityConversion'

const CentralKitchenInventoryCalculator = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem('token');
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [fetchOnLoad, setFetchOnLoad] = useState(false);
    const [includeSubRecipe, setIncludeSubRecipe] = useState(true);
    const [openingDate, setOpeningDate] = useState();
    const [checkEndDate, setCheckEndDate] = useState();

    const [dateLoading, setDateLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState(null);
    const [consumptionData, setConsumptionData] = useState(null);
    const [inventoryData, setInventoryData] = useState([]);
    const [itemSearchTerm, setItemSearchTerm] = useState("");
    const [hasPhysical, setHasPhysical] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [itemDetails, setItemDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState("");

    const loading =
        dateLoading ||
        searchLoading ||
        inventoryLoading ||
        saveLoading;

    const [savedCentralKitchenPhysicals, setsavedCentralKitchenPhysicals] = useState(() => {
        try {
            const raw = localStorage.getItem('savedCentralKitchenPhysical');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
    };

    const fetchDates = async () => {
        if (!token || !selectedOutlet) return;
        setStartDate(null);
        setEndDate(null);

        try {
            setDateLoading(true);

            const response = await axios.post(`${baseUrl}/get-inventory-period-production`, {
                token: token,
                outlet: selectedOutlet
            });

            const opening_date = response.data?.opening_date
            const closing_date = response.data?.closing_date
            setOpeningDate(opening_date);

            setStartDate(opening_date);
            setEndDate(closing_date);

            setError(null);
            setFetchOnLoad(true);

        } catch (err) {
            setError(err?.response?.data?.message || err?.response?.data?.error);
            toast.error("Failed to fetch dates");
        } finally {
            setDateLoading(false);
        }
    };

    useEffect(() => {
        fetchDates();
    }, [token, selectedOutlet])

    useEffect(() => {
        if (fetchOnLoad) {
            handleSearch();
            fetchInventoryData();
            setFetchOnLoad(false);
        }
    }, [token, selectedOutlet, includeSubRecipe, fetchOnLoad])

    const handleSearch = async (start = startDate, end = endDate) => {
        if (!token || !selectedOutlet || !start || !end) return;

        try {
            setSearchLoading(true);
            const response = await axios.post(`${baseUrl}/production-consumption`, {
                startDate: start,
                endDate: end,
                token: token,
                includeSubRecipeItems: !includeSubRecipe,
                outlet: selectedOutlet
            });
            const data = response.data;
            setConsumptionData(data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || "Failed to fetch data");
            setConsumptionData(null);
            toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch data");
        } finally {
            setSearchLoading(false);
        }
    };

    const fetchInventoryData = async () => {
        if (!token || !selectedOutlet) return;
        if (!endDate || !startDate) return;

        try {
            setInventoryLoading(true);

            const res = await axios.post(`${baseUrl}/inventory-summary-production`, {
                token,
                includeSubRecipeItems: includeSubRecipe,
                outlet: selectedOutlet,
                startDate: startDate,
                endDate: endDate
            });

            const data = res.data.summary || {}

            setInventoryData(data);
            setHasPhysical(data.has_physical_before_start);
            setCheckEndDate(endDate);
            setError(null);

        } catch (err) {
            const errorMsg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err.message ||
                "Failed to fetch data";

            setError(errorMsg);
            setInventoryData([]);
            toast.error(errorMsg);

        } finally {
            setInventoryLoading(false);
        }
    };

    const handleFetchDetails = async (itemName) => {
        try {
            setDetailsLoading(true);
            setDetailsError("");
            setItemDetails(null);
            setShowModal(true);

            const response = await axios.post(
                `${baseUrl}/itemconsumefromproduction`,
                {
                    token: token,
                    outlet: selectedOutlet,
                    item_name: itemName,
                    startDate: startDate,
                    endDate: endDate,
                }
            );

            const data = response.data || {};

            setItemDetails(data);
        } catch (error) {
            console.log(error);

            setDetailsError(
                error?.response?.data?.message ||
                "Failed to fetch details"
            );

            toast.error("Failed to fetch details");
        } finally {
            setDetailsLoading(false);
        }
    };

    const totalItems = Number(consumptionData?.summary?.total_production_recipe);
    const uniqueItems = Number(consumptionData?.summary?.unique_items_sold);
    const missingItems = totalItems - uniqueItems;
    const itemAccuracy = uniqueItems / totalItems * 100;

    const handleSave = () => {
        if (startDate !== openingDate) {
            toast.error(
                `Start date mismatch. Selected: ${startDate}, Expected (Opening): ${openingDate}. Please search before saving.`
            );
            return;
        }

        if (endDate !== checkEndDate) {
            toast.error(
                `End date mismatch. Selected: ${endDate}, Expected (Closing): ${checkEndDate}. Please search before saving.`
            );
            return;
        }

        handleSubmit();
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>

                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();

                            if (!token || !selectedOutlet) return;

                            try {
                                setSaveLoading(true);

                                const items = Object.entries(inventoryData).map(
                                    ([name, item]) => {
                                        const physicalCount = getValidPhysical(
                                            name,
                                            savedCentralKitchenPhysicals
                                        );

                                        return {
                                            name: name,

                                            type: item.type,

                                            group_name: item.group_name,

                                            uom: item.uom,

                                            new_uom: item.new_uom || item.uom,

                                            total_consumed: calculateConsumed(
                                                item,
                                                physicalCount
                                            ),

                                            total_received:
                                                Number(item.total_received) || 0,

                                            closing: calculateClosing(
                                                item,
                                                physicalCount
                                            ),

                                            physical_count: calculatePhysical(
                                                item,
                                                physicalCount
                                            ),

                                            opening_count:
                                                Number(item.opening) || 0,

                                            total_wastage:
                                                Number(item.total_wasted) || 0,

                                            discrepancy_count:
                                                calculateDiscrepancy(
                                                    item,
                                                    physicalCount
                                                )
                                        };
                                    }
                                );

                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    start_date: startDate,
                                    end_date: endDate,
                                    items: items,
                                    physical_added: !areAllCountsEmpty(),
                                    total_items: totalItems,
                                    accuracy: itemAccuracy,
                                    missing_items: missingItems
                                };

                                await axios.post(`${baseUrl}/save-inventory-snapshot-production`, payload);

                                localStorage.removeItem('savedCentralKitchenPhysical');
                                setsavedCentralKitchenPhysicals([]);
                                setInventoryData([]);

                                fetchDates();

                                toast.success("Inventory snapshot saved successfully");

                            } catch (err) {
                                console.error("Error saving inventory:", err);

                                const errorMessage =
                                    err?.response?.data?.error ||
                                    err?.response?.data?.message ||
                                    err?.error ||
                                    "Failed to save inventory snapshot";

                                toast.error(errorMessage);

                            } finally {
                                setSaveLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-inventory-data'
            }
        );
    };

    const formattedDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
        const day = date.getDate();
        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return `${n}th`;
            switch (n % 10) {
                case 1: return `${n}st`;
                case 2: return `${n}nd`;
                case 3: return `${n}rd`;
                default: return `${n}th`;
            }
        };
        const dayWithOrdinal = getOrdinal(day);
        const month = date.toLocaleDateString("en-US", { month: "long" });
        const year = date.getFullYear();

        return `${weekday}, ${dayWithOrdinal} ${month}, ${year}`;
    };

    const physicalMap = useMemo(() => {
        return Object.fromEntries(
            savedCentralKitchenPhysicals.map(i => [i.item_name, Number(i.quantity)])
        );
    }, [savedCentralKitchenPhysicals]);

    const getValidPhysical = (itemName) => {
        const num = physicalMap[itemName];
        return Number.isFinite(num) ? num : null;
    };

    const calculateConsumed = (item, physicalCount) => {
        const totalConsumed = Number(item.total_consumed) || 0;
        const totalOpening = Number(item.opening) || 0;
        const totalReceived = Number(item.total_received) || 0;
        const totalWastage = Number(item.total_wasted) || 0;

        if (
            totalConsumed === 0 &&
            physicalCount !== null &&
            (totalOpening > 0 || totalReceived > 0)
        ) {
            return totalOpening + totalReceived - physicalCount - totalWastage;
        }

        return totalConsumed;
    };

    const calculateClosing = (item, physicalCount) => {
        const totalConsumed = Number(item.total_consumed) || 0;
        const totalOpening = Number(item.opening) || 0;
        const totalReceived = Number(item.total_received) || 0;

        if (
            totalConsumed === 0 &&
            physicalCount !== null &&
            (totalOpening > 0 || totalReceived > 0)
        ) {
            return physicalCount;
        }

        return Number(item.closing) || 0;
    };

    const calculatePhysical = (item, physicalCount) => {
        if (physicalCount !== null) return physicalCount;
        return Number(item.closing) || 0;
    };

    const calculateDiscrepancy = (item, physicalCount) => {
        if (physicalCount !== null) {
            return physicalCount - (calculateClosing(item, physicalCount));
        }
        return 0;
    };

    const areAllCountsEmpty = () => {
        return savedCentralKitchenPhysicals.every(entry => {
            const qty = Number(entry?.quantity);
            return qty === 0 || isNaN(qty);
        });
    };

    const handleOpeningClick = () => {
        navigate('/central-kitchen/opening-count', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd"
            }
        });
    };

    const handlePhysicalClick = () => {
        navigate('/central-kitchen/physical-count', {
            state: {
                inventoryData,
                selectedOutlet,
                accessToken: "a7fK2pX9Zd"
            }
        });
    }

    const UNIT_LABELS = {
        ...Object.fromEntries(
            kgVariations.map((u) => [u.toLowerCase(), "kg"])
        ),

        ...Object.fromEntries(
            gramVariations.map((u) => [u.toLowerCase(), "gms"])
        ),

        ...Object.fromEntries(
            literVariations.map((u) => [u.toLowerCase(), "ltr"])
        ),

        ...Object.fromEntries(
            milliliterVariations.map((u) => [u.toLowerCase(), "ml"])
        ),

        can: "can",
        cans: "can",
        tin: "can",
        tins: "can",

        box: "box",
        boxes: "box",

        btl: "btl",
        bottle: "btl",
        bottles: "btl",

        bag: "bag",
        bags: "bag",

        pkt: "pkt",
        packet: "pkt",
        packets: "pkt",

        carton: "ctn",
        ctn: "ctn",
        cartons: "ctn",

        dozen: "doz",
        doz: "doz",

        unit: "unit",
        units: "unit",

        pcs: "pcs",
        piece: "pcs",
        pieces: "pcs",

        nos: "nos",
        no: "nos",

        bunch: "bunch",
        bunches: "bunch",

        roll: "roll",
        rolls: "roll"
    };

    const getLabel = (uomString) => {
        if (!uomString) return "";
        return UNIT_LABELS[uomString.trim().toLowerCase()] ?? uomString.trim();
    };

    const getConversionFactor = (fromUom, toUom) => {
        if (!fromUom || !toUom) return 1;

        const from = fromUom.trim().toLowerCase();
        const to = toUom.trim().toLowerCase();

        if (from === to) return 1;

        // kg → gm
        if (kgVariations.includes(from) && gramVariations.includes(to)) return 1000;
        // gm → kg
        if (gramVariations.includes(from) && kgVariations.includes(to)) return 0.001;
        // ltr → ml
        if (literVariations.includes(from) && milliliterVariations.includes(to)) return 1000;
        // ml → ltr
        if (milliliterVariations.includes(from) && literVariations.includes(to)) return 0.001;

        // No known conversion — return 1 and let it pass through as-is
        return 1;
    };

    const formatQuantity = (item, quantity) => {
        if (quantity === null || quantity === undefined || isNaN(quantity)) {
            return "0";
        }

        const parsedQty = parseFloat(quantity);
        if (parsedQty === 0) return "0";

        const qty = Math.abs(parsedQty);
        const sign = parsedQty < 0 ? "-" : "";

        const baseLabel = getLabel(item?.uom);
        const newUomLabel = getLabel(item?.new_uom);
        const unitdefUom = item?.unitdefinition?.uom ?? null;
        const multiplier = parseFloat(item?.unitdefinition?.unit ?? null);

        if (!multiplier || isNaN(multiplier) || !unitdefUom) {
            return `${sign}${qty} ${baseLabel}`;
        }

        const sameUnit = !newUomLabel || newUomLabel === baseLabel;
        const subLabel = sameUnit ? getLabel(unitdefUom) : newUomLabel;

        if (subLabel === baseLabel) {
            return `${sign}${qty} ${baseLabel}`;
        }

        // Determine conversion factor between unitdefinition.uom and the resolved subLabel
        const conversionFactor = getConversionFactor(unitdefUom, sameUnit ? unitdefUom : item?.new_uom);
        const effectiveMultiplier = multiplier * conversionFactor;

        // effectiveMultiplier < 1 means the sub-unit is LARGER than the base unit
        // e.g. uom=grms, unitdef=0.001kg → 1 grm = 0.001 kg
        // In this case convert the entire qty into the larger unit and split whole/remainder
        if (effectiveMultiplier < 1) {
            const totalInSubUnit = qty * effectiveMultiplier;        // total expressed in sub-unit
            const wholeInSubUnit = Math.floor(totalInSubUnit);       // whole sub-units
            const remainderInBase = Math.round(                       // leftover back in base unit
                (totalInSubUnit - wholeInSubUnit) / effectiveMultiplier
            );

            if (wholeInSubUnit === 0) {
                return `${sign}${remainderInBase} ${baseLabel}`;
            }
            if (remainderInBase === 0) {
                return `${sign}${wholeInSubUnit} ${subLabel}`;
            }
            return (
                <div>
                    {sign}{wholeInSubUnit} {subLabel} + {remainderInBase} {baseLabel}
                </div>
            );
        }

        // effectiveMultiplier >= 1 — normal path, sub-unit is smaller than base unit
        if (Number.isInteger(qty)) {
            return `${sign}${qty} ${baseLabel}`;
        }

        const wholePart = Math.floor(qty);
        const decimalPart = qty - wholePart;
        const subUnits = Math.round(decimalPart * effectiveMultiplier);

        if (wholePart === 0) return `${sign}${subUnits} ${subLabel}`;
        if (subUnits === 0) return `${sign}${wholePart} ${baseLabel}`;

        return (
            <div>
                {sign}{wholePart} {baseLabel} + {subUnits} {subLabel}
            </div>
        );
    };

    const areAllOpeningCountsZero = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return false;
        }

        return Object.values(data).every(
            (item) => (item.opening ?? 0) === 0
        );
    };

    const allZero = areAllOpeningCountsZero(inventoryData);

    const getButtonStyle = (color) => {
        const colors = {
            slate: "bg-gradient-to-r from-slate-400 to-slate-700",
            blue: "bg-gradient-to-r from-blue-400 to-blue-700",
            green: "bg-gradient-to-r from-green-400 to-green-700",
            yellow: "bg-gradient-to-r from-yellow-400 to-yellow-600",
            orange: "bg-gradient-to-r from-orange-400 to-orange-700",
            sky: "bg-gradient-to-r from-sky-400 to-sky-700",
            pink: "bg-gradient-to-r from-pink-400 to-pink-700",
            purple: "bg-gradient-to-r from-purple-400 to-purple-700",
            teal: "bg-gradient-to-r from-teal-400 to-teal-700",
        };

        return `
        ${colors[color]}
        text-white
        px-4 py-2
        text-sm font-semibold
        rounded-md
        transition
        hover:scale-[1.03]
        hover:shadow-lg
        active:scale-95
        disabled:opacity-60 disabled:cursor-not-allowed
    `;
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    const convertToNewBaseQuantity = ({
        quantity,
        unit,
        fromUom,
        toUom,
        kgVars,
        gVars,
        lVars,
        mlVars
    }) => {
        let q = parseFloat(quantity);
        let u = Number(unit);

        const from = normalizeUOM(fromUom, kgVars, gVars, lVars, mlVars);
        const to = normalizeUOM(toUom, kgVars, gVars, lVars, mlVars);

        if (!to || from === to) return q;

        q = q * u;

        if (from === 'kg' && to === 'g') return q * 1000;
        if (from === 'g' && to === 'kg') return q / 1000;

        if (from === 'ltr' && to === 'ml') return q * 1000;
        if (from === 'ml' && to === 'ltr') return q / 1000;

        return q
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex-1 p-3">
                <div className="flex justify-center mb-2">
                    <OutletSearch
                        selectedOutlet={selectedOutlet}
                        setSelectedOutlet={setSelectedOutlet}
                        token={token}
                    />
                </div>

                <div className='flex items-end gap-20'>
                    <div className="flex items-center gap-3">
                        <span>From:</span>
                        <input
                            type="date"
                            className="border p-1 rounded"
                            value={startDate}
                            onChange={(e) => handleDateChange(e, 'start')}
                        />

                        <span>To:</span>
                        <input
                            type="date"
                            className="border p-1 rounded"
                            value={endDate}
                            onChange={(e) => handleDateChange(e, 'end')}
                        />
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type="checkbox"
                            checked={includeSubRecipe}
                            onChange={() => {
                                setFetchOnLoad(true)
                                setIncludeSubRecipe(!includeSubRecipe)
                            }}
                        />
                        <label>Include Sub-recipe?</label>
                    </div>

                    <button
                        onClick={() => {
                            handleSearch(startDate, endDate);
                            fetchInventoryData();
                        }}
                        disabled={loading}
                        className="px-4 py-2 flex gap-1.5 items-center rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Search
                    </button>
                </div>

                <PageStatus
                    loading={loading}
                    error={error}
                    loadingText="Loading inventory data..."
                />

                {!loading && !error && (
                    <>
                        <CentralInventoryConsumptionTable
                            consumptionData={consumptionData}
                            convertToNewBaseQuantity={convertToNewBaseQuantity}
                        />

                        <div className="mt-8 flex items-center gap-10">
                            <div className='w-80 z-50'>
                                <Select
                                    className="react-select-container w-full"
                                    classNamePrefix="react-select"
                                    options={
                                        inventoryData &&
                                            Object.keys(inventoryData).length > 0
                                            ? Object.entries(inventoryData).map(
                                                ([itemName, item]) => ({
                                                    value: itemName,
                                                    label: itemName
                                                })
                                            )
                                            : []
                                    }
                                    isClearable
                                    isSearchable
                                    placeholder="Search item..."
                                    onChange={(selectedOption) => setItemSearchTerm(selectedOption?.value || "")}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: '40px',
                                        }),
                                    }}
                                />
                            </div>

                            <div className='flex gap-10 items-center'>
                                <button
                                    onClick={handleSave}
                                    disabled={saveLoading || allZero}
                                    className={getButtonStyle("green")}
                                >
                                    Save
                                </button>

                                <button
                                    onClick={handleOpeningClick}
                                    disabled={saveLoading || hasPhysical}
                                    className={getButtonStyle("blue")}
                                >
                                    Add Opening
                                </button>

                                <button
                                    className={getButtonStyle("pink")}
                                    disabled={saveLoading}
                                    onClick={() => handleNavigate('/central-kitchen/wastage')}
                                >
                                    Add Wastage
                                </button>

                                <button
                                    onClick={handlePhysicalClick}
                                    disabled={saveLoading || allZero}
                                    className={getButtonStyle("sky")}
                                >
                                    Physical Count
                                </button>

                                <button
                                    onClick={() => handleNavigate('/central-kitchen/snapshot')}
                                    className={getButtonStyle("yellow")}
                                >
                                    Costing Analysis
                                </button>

                                {/* <button
                                    onClick={handleReceivedClick}
                                    className={`border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-3 py-2 text-sm`}
                                >
                                    Receive Stocks
                                </button> */}
                            </div>
                        </div>

                        <CentralInventorySummaryTable
                            inventoryData={inventoryData}
                            startDate={startDate}
                            endDate={endDate}
                            formattedDate={formattedDate}
                            itemSearchTerm={itemSearchTerm}
                            getValidPhysical={getValidPhysical}
                            formatQuantity={formatQuantity}
                            calculateConsumed={calculateConsumed}
                            calculateClosing={calculateClosing}
                            calculateDiscrepancy={calculateDiscrepancy}
                            handleFetchDetails={handleFetchDetails}
                        />
                    </>
                )}

                <CentralKitchenInventoryModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    itemDetails={itemDetails}
                    detailsLoading={detailsLoading}
                    detailsError={detailsError}
                />
            </div>

            <Footer />
        </div>
    )
}

export default CentralKitchenInventoryCalculator